import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { usePreset } from '@atlaskit/editor-core/use-preset';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import type { EditorProps } from '@atlaskit/editor-core';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension';
import { usePreviousState } from '@atlaskit/editor-common/hooks';
import { type ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel';

import type { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';

import { useIsMentionOnRestrictedPageEligible } from '@confluence/experiment-mention-on-restricted-page';
import type { LoomProviderConfig } from '@confluence/fabric-providers';
import { expValNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import {
	type MentionType,
	useMentionsNotifications,
	handleMentionsChanged,
} from '@confluence/mentions-notifications';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api';
import { useSessionData } from '@confluence/session-data';
import type { WithFlagsProps } from '@confluence/flags';
import type { EditorAPI } from '@confluence/loom-editor-utils';
import { useInitializeLoomPlugin, useLoomPluginOptions } from '@confluence/loom-editor-utils';
import {
	useEditorEngagementPlatform,
	useEditorPluginAIDefinitionsConfig,
	useEditorPluginAIDefinitionsEnabled,
	useEditorPluginAIProactiveConfig,
	useEditorPluginAIProactiveEnabled,
	useSelectionExtensionPluginOptions,
} from '@confluence/editor-features';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import { isConfluenceRedirectToLoomSDKExperimentEnabled } from '@confluence/growth-experiment-helpers';
import type { AIGlobalOptIn, EditorPluginAIFeedbackHandlersRef } from '@confluence/editor-features';
import type { FullPageAppearances } from '@confluence/content-appearance';
import { useIsSidePanelOpen } from '@confluence/side-panel-state-container';
import { getPageMode } from '@confluence/jira-work-create-extension';
import type { PageMode } from '@confluence/issue-create-side-panel';

import { useEditorPreferencesProvider } from '../utils/EditorPreferences';

import { EditorLoomButton as Experiment_RedirectToSdkAfterCrossflow_EditorLoomButton } from './Experiment_RedirectToSdkAfterCrossflow/EditorLoomButton';
import { EditorLoomButton } from './EditorLoomButton';
import type { FullPageEditorPresetOptions } from './createFullPageEditorPreset';
import { createFullPageEditorPreset } from './createFullPageEditorPreset';
import { createFullPageEditorPresetNext } from './createFullPageEditorPresetNext';

export interface FullPageEditorPresetProps extends WithFlagsProps {
	appearance: FullPageAppearances;
	featureFlags: EditorProps['featureFlags'];
	onSave: EditorProps['onSave'];
	linking: EditorProps['linking'];
	contextIdentifierProvider: EditorProps['contextIdentifierProvider'];
	media: EditorProps['media'];
	placeholder: EditorProps['placeholder'];
	emojiProvider: EditorProps['emojiProvider'];
	annotationProviders: EditorProps['annotationProviders'];
	autoformattingProvider: EditorProps['autoformattingProvider'];
	taskDecisionProvider: EditorProps['taskDecisionProvider'];
	// collabEditProvider: EditorProps['collabEditProvider'];
	mentionProvider: EditorProps['mentionProvider'];
	collabEdit: EditorProps['collabEdit'];
	primaryToolbarComponents: EditorProps['primaryToolbarComponents'];
	waitForMediaUpload: EditorProps['waitForMediaUpload'];
	mention: EditorProps['mention'];
	allowFindReplace: EditorProps['allowFindReplace'];
	allowExtension: EditorProps['allowExtension'];
	extensionHandlers: EditorProps['extensionHandlers'];
	referentialityContext: ReferentialityContext;
	editorPluginAIOptions: ReturnType<typeof createPageEditorPluginAIOptions>;
	aiGlobalOptIn: {
		status: AIGlobalOptIn['optInStatus'];
		triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	};
	editorPluginAIFeedbackHandlers: React.RefObject<EditorPluginAIFeedbackHandlersRef>;
	elementBrowser: EditorProps['elementBrowser'];
	quickInsert: EditorProps['quickInsert'];
	setEditorAPI?: (editorAPI?: FullPageEditorPresetAPINext) => void;
	viewMode?: ViewMode;
	hasEditPermission?: boolean;
	requestToEditContent?: () => void;
	hasRequestedEditPermission?: boolean;
	getViewPagePath: () => string | null;
	__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'];
	__livePage?: boolean;
	contextPanelOptions?: ContextPanelPluginOptions;
	contentId: string | undefined;
	spaceId: string | undefined;
	isAIEnabledForContent: boolean;
}

export type FullPageEditorPresetAPINext = ExtractPresetAPI<
	ReturnType<typeof useFullPageEditorPreset>
>;

// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function useFullPageEditorPreset(props: FullPageEditorPresetProps): any {
	const { showObjectSidebar, hideObjectSidebar, hideObjectSidebarByPanelId } =
		useObjectSidebarActions();

	const isLivePage = useIsCurrentPageLive();
	const { isDesktop } = useIsDesktop();
	const { isVariantCohort: isConfluenceRedirectToLoomSDKExperiment } =
		isConfluenceRedirectToLoomSDKExperimentEnabled(); // evaluate experiment here but does NOT fire exposure event

	const { userId } = useSessionData();
	const [
		{
			deletedMentions,
			addedMentions,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			addedTasks,
			deletedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
		},
		{
			setDeletedMentions,
			setAddedMentions,
			setAddedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();
	const [mentionChanges, setMentionChanges] = useState<MentionType[]>([]);
	const previousEditorProps = usePreviousState(props);
	const isClassicMentionsNotificationsEnrolled =
		useIsMentionOnRestrictedPageEligible() &&
		expValNoExposure('cc_onboarding_mention_on_restricted_page', 'cohort', 'not-enrolled') !==
			'not-enrolled';

	const isLivePageRef = useRef<boolean>(isLivePage);
	isLivePageRef.current = isLivePage;

	const getCurrentIsLivePage = useCallback(() => {
		return isLivePageRef.current;
	}, []);

	const { confluenceEditorPreferencesProvider } = useEditorPreferencesProvider({
		getCurrentIsLivePage,
	});

	const isSidePanelOpen = useIsSidePanelOpen();
	const isSidePanelOpenRef = useRef<boolean>(isSidePanelOpen);
	isSidePanelOpenRef.current = isSidePanelOpen;

	const pageMode = getPageMode(props.viewMode, props.__livePage, props.hasEditPermission);
	const pageModeRef = useRef<PageMode>(pageMode);
	pageModeRef.current = pageMode;

	useEffect(() => {
		const isMentionNotificationsEnabled =
			isLivePage || (!isLivePage && isClassicMentionsNotificationsEnrolled);

		if (mentionChanges.length && isMentionNotificationsEnabled) {
			handleMentionsChanged({
				mentionChanges,
				deletedMentions,
				addedMentions,
				setDeletedMentions,
				setAddedMentions,
				userId,
				unrestrictedMentions,
				parentSpaceRestrictedMentions,
				pageRestrictedMentions,
				unrestrictedTasks,
				addedTasks,
				deletedTasks,
				setAddedTasks,
				setDeletedTasks,
				pageRestrictedTasks,
				parentSpaceRestrictedTasks,
				setMentionsAdded,
				setTasksAdded,
			});
			setMentionChanges([]);
		}
		// We only need to trigger the handler function when mentionChanges gets updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionChanges]);

	const editorAPIRef = useRef<FullPageEditorPresetAPINext | null | undefined>(null);

	const { isPluginEnabled: isLoomPluginEnabled, pluginOptions: loomPluginOptions } =
		useLoomPluginOptions({
			EditorButtonComponent: isConfluenceRedirectToLoomSDKExperiment
				? Experiment_RedirectToSdkAfterCrossflow_EditorLoomButton
				: EditorLoomButton,
			editorAPIRef,
			// [ED-27497] IMPORTANT: If you change the code below, please also update the
			// platform/packages/editor/editor-presets-confluence/src/presets/full-page/loomPluginOptions.ts
			...(fg('confluence_frontend_object_sidebar')
				? { editorShouldShowToolbarLoomButton: false }
				: {}),
		});

	const { config: editorEngagementPlatformPluginConfig } = useEditorEngagementPlatform();

	const editorPluginAIDefinitionsEnabled = useEditorPluginAIDefinitionsEnabled();
	const editorPluginAIDefinitionsConfig = useEditorPluginAIDefinitionsConfig();

	const editorPluginAIProactiveEnabled = useEditorPluginAIProactiveEnabled({
		aiGlobalOptInStatus: props.aiGlobalOptIn.status,
	});

	const editorPluginAIProactiveConfig = useEditorPluginAIProactiveConfig({
		aiGlobalOptIn: props.aiGlobalOptIn,
		editorPluginAIFeedbackHandlers: props.editorPluginAIFeedbackHandlers,
	});

	const selectionExtensionPluginOptions = useSelectionExtensionPluginOptions({
		isSidePanelOpenRef,
		pageModeRef,
	});

	const intl = useIntl();

	// Memoise the preset otherwise we will re-render the editor too often
	const { preset, editorApi } = usePreset(() => {
		const { editorPluginAIOptions, aiGlobalOptIn } = props;

		const presetOptions: FullPageEditorPresetOptions = {
			...props,
			editorPluginAIOptions,
			aiGlobalOptIn,
			loomPluginOptions,
			isLoomPluginEnabled,
			userPreferencesProvider: confluenceEditorPreferencesProvider,
			allowExtension: props.allowExtension,
			prevAppearance: previousEditorProps?.appearance,
			handleMentionsChanged: (changes: MentionType[]) => {
				if (changes.length) {
					setMentionChanges(changes);
				}
			},
			editorPluginAIDefinitionsConfig,
			editorPluginAIDefinitionsEnabled,
			editorPluginAIProactiveEnabled,
			editorPluginAIProactiveConfig,
			editorEngagementPlatformPluginConfig,
			isDesktop,
			initialPluginConfiguration: {
				// IMPORTANT: If you change the code below, please also update the
				// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/editorViewModePluginOptions.ts
				viewModePlugin: {
					initialContentMode: props.__livePage
						? props.hasEditPermission
							? 'live-edit'
							: 'live-view-only'
						: 'edit',
				},
			},
			// If object-sidebar is not enabled, then we don't need to pass the contextPanelOptions
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			...(fg('confluence_frontend_object_sidebar') &&
			fg('platform_editor_ai_object_sidebar_injection')
				? {
						contextPanelOptions: {
							objectSideBar: {
								showPanel: showObjectSidebar,
								closePanel: hideObjectSidebar,
								closePanelById: hideObjectSidebarByPanelId,
							},
						},
					}
				: {}),

			// [ED-27497] Moved to platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/basePluginOptions.ts
			removeScrollGutter:
				!fg('confluence-frontend-redesigned-footer-comment') &&
				isLivePage &&
				fg('confluence_frontend_comments_panel_v2'),
			selectionExtensionPluginOptions,
		};
		return fg('platform-editor-use-atlassian-confluence-presets')
			? createFullPageEditorPresetNext({ ...presetOptions, intl })
			: createFullPageEditorPreset(presetOptions);
		// WARNING: Adding this dependency array causes the editor plugins to all recreate
		// This can be very expensive. Be very careful adding to this, avoid if possible!
		// Generally we can update props via their plugin APIs (see `placeholder` as an example)
	}, [
		fg('platform_editor_remove_fp_preset_dependencies') ? undefined : props.aiGlobalOptIn.status,
		fg('platform_editor_remove_fp_preset_dependencies') ? undefined : isLoomPluginEnabled,
		fg('platform_editor_remove_fp_preset_dependencies') ? undefined : loomPluginOptions,
		fg('platform_editor_remove_fp_preset_dependencies') ? undefined : props.getViewPagePath,
		fg('platform_editor_remove_fp_preset_dependencies')
			? undefined
			: editorEngagementPlatformPluginConfig,
		// update preset based on props change, originally from packages/editor/editor-core/src/create-editor/preset-utils.ts
		// persistScrollGutter not used, allowUndoRedoButtons and sanitizePrivateContent always true
		props.appearance,
	]);

	useEffect(() => {
		editorApi?.core?.actions.execute(
			editorApi?.placeholder?.commands.setPlaceholder(props.placeholder),
		);
	}, [editorApi, props.placeholder]);

	const { setEditorAPI } = props;

	useEffect(() => {
		if (!setEditorAPI) {
			return;
		}
		editorAPIRef.current = editorApi;
		setEditorAPI(editorApi);
	}, [editorApi, setEditorAPI]);

	useEffect(() => {
		if (props.viewMode === undefined) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.editorViewMode?.commands?.updateViewMode(props.viewMode),
		);
	}, [props.viewMode, editorApi]);

	useEffect(() => {
		if (!fg('editor_request_to_edit_task')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateEditPermission(props.hasEditPermission),
		);
	}, [props.hasEditPermission, editorApi]);

	useEffect(() => {
		if (!fg('editor_request_to_edit_task')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.taskDecision?.commands?.updateHasRequestedEditPermission(
				props.hasRequestedEditPermission || false,
			),
		);
	}, [props.hasRequestedEditPermission, editorApi]);

	const { enabled, autoHighlightEnabled, enablementSource } = editorPluginAIDefinitionsConfig;
	useEffect(() => {
		if (!fg('confluence_ai_access_policy')) {
			return;
		}
		editorApi?.core?.actions?.execute(
			editorApi?.aiDefinitions?.commands?.updateState({
				enabled,
				autoHighlightEnabled,
				enablementSource,
			}),
		);
	}, [editorApi, enabled, autoHighlightEnabled, enablementSource]);

	const { openLoomRecorder } = useLoomRecorderEntryPoint({
		flags: props.flags,
		entryPointLocation: 'editor-toolbar',
		resetInsertFunctionOnUnmount: true,
	});

	const loomProviderConfig: LoomProviderConfig = useMemo(
		() => ({ openLoomRecorder }),
		[openLoomRecorder],
	);

	useInitializeLoomPlugin({
		loomPlugin: editorApi?.loom as EditorAPI['loom'],
		providerConfig: loomProviderConfig,
	});

	return preset;
}
